// ui/src/components/shared/PersonDrawLedger.jsx

/**
 * @file PersonDrawLedger.jsx
 * @description Draw ledger viewer for a single person (personUuid). Fetches
 *   draw ledger entries from ep 47 (GET /api/v1/draw-ledger/{personUuid}).
 *   Displays current balance, entry history, and handles multiple null fields
 *   that are schema gaps in r59 (ledgerStatus, openedEffectiveDate, approvedBy,
 *   approvedAt, currency, planVersion). Accessible to manager (own team) and
 *   rep (own record) roles per RBAC on ep 47.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileB_v4_final.md §5.3 — GET /api/v1/draw-ledger/{personUuid} (ep 47)
 * @migration OI-S773-DRAW-LEDGER-M-TC-10 — ledgerStatus, openedEffectiveDate,
 *   approvedBy, approvedAt all null until m_tc_10/11 applied. Render gracefully.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fullDateTime } from './ProvisionalBadge';

/**
 * @typedef {{
 *   entryId: string,
 *   effectiveDate: string,
 *   fiscalPeriodLabel: string,
 *   entryType:
 *     'advance' |
 *     'recovery' |
 *     'forgiveness' |
 *     'adjustment' |
 *     'conversion' |
 *     'settlement' |
 *     'transfer',
 *   amount: number,
 *   currencyCode: string,
 *   balanceAfter: number,
 *   planLabel?: string,
 *   reasonText?: string,
 *   approvedBy?: string,
 *   approvedAt?: string,
 * }} DrawLedgerEntry
 */

/**
 * @typedef {{
 *   personUuid: string,
 *   currentBalance: number,
 *   currencyCode: string,
 *   programCode: string | null,
 *   openedEffectiveDate: string,
 *   ledgerStatus: 'open' | 'closed' | 'transferred' | 'settled',
 *   entries: DrawLedgerEntry[]
 * }} DrawLedgerResponse
 */

/**
 * @param {{
 *   personUuid: string,
 *   fiscalPeriodId: number,
 *   mode: 'manager' | 'rep',
 *   onEscalate?: (context: {
 *     personUuid: string,
 *     fiscalPeriodId: number,
 *     currentBalance: number,
 *     currencyCode: string,
 *     ledgerStatus: string
 *   }) => void,
 *   className?: string
 * }} props
 */
export default function PersonDrawLedger({
  personUuid,
  fiscalPeriodId,
  mode,
  onEscalate,
  className = '',
}) {
  const mountedRef = useRef(true);

  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/v1/draw-ledger/${encodeURIComponent(
          personUuid,
        )}?period=${encodeURIComponent(fiscalPeriodId)}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          credentials: 'include',
        },
      );

      if (!mountedRef.current) return;

      if (!response.ok) {
        let message = 'Unable to load draw balance ledger.';

        try {
          const payload = await response.json();
          if (!mountedRef.current) return;

          if (payload?.detail) {
            message =
              typeof payload.detail === 'string'
                ? payload.detail
                : payload.detail?.message || message;
          }
        } catch {
          // Preserve the safe generic error message.
        }

        throw new Error(message);
      }

      const payload = await response.json();
      if (!mountedRef.current) return;

      setLedger(payload);
    } catch (loadError) {
      if (!mountedRef.current) return;

      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Unable to load draw balance ledger.',
      );
      setLedger(null);
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
    }
  }, [personUuid, fiscalPeriodId]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger, reloadToken]);

  function handleRetry() {
    setReloadToken((value) => value + 1);
  }

  if (loading) {
    return <LedgerSkeleton className={className} />;
  }

  if (error) {
    return (
      <section
        aria-label="Draw balance ledger"
        className={[
          'rounded-[12px]',
          'border border-[rgb(var(--color-border))]',
          'bg-[rgb(var(--color-surface))]',
          'p-4',
          className,
        ].join(' ')}
      >
        <div
          role="alert"
          className="modifier-manager-inline-error text-[12px]"
        >
          {error}
        </div>

        <button
          type="button"
          className="secondary-button mt-3"
          onClick={handleRetry}
        >
          Retry
        </button>
      </section>
    );
  }

  if (!ledger) {
    return (
      <section
        aria-label="Draw balance ledger"
        className={[
          'rounded-[12px]',
          'border border-[rgb(var(--color-border))]',
          'bg-[rgb(var(--color-surface))]',
          'p-4',
          className,
        ].join(' ')}
      >
        <p className="text-[12px] text-[rgb(var(--color-text-muted))]">
          No draw or guarantee is configured for this plan assignment.
        </p>
      </section>
    );
  }

  const entries = Array.isArray(ledger.entries) ? ledger.entries : [];

  return (
    <section
      aria-label="Person draw balance ledger"
      className={[
        'overflow-hidden rounded-[12px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface))]',
        className,
      ].join(' ')}
    >
      <LedgerHeader
        ledger={ledger}
        mode={mode}
        personUuid={personUuid}
        fiscalPeriodId={fiscalPeriodId}
        onEscalate={onEscalate}
      />

      <div className="overflow-x-auto">
        <table role="table" className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-[rgb(var(--color-surface-muted))]">
              <th
                scope="col"
                className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]"
              >
                Period
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]"
              >
                Balance after
              </th>
            </tr>
          </thead>

          <tbody>
            {entries.map((entry) => (
              <LedgerRow
                key={entry.entryId}
                entry={entry}
                mode={mode}
              />
            ))}
          </tbody>
        </table>
      </div>

      {entries.length === 0 && (
        <div className="border-t border-[rgb(var(--color-border))] px-4 py-4">
          <p className="text-[12px] text-[rgb(var(--color-text-muted))]">
            The person-level ledger exists with no posted activity in this
            view. A zero current balance does not imply that a prior balance was
            reset.
          </p>
        </div>
      )}

      <div className="border-t border-[rgb(var(--color-border))] px-4 py-3">
        <p className="text-[11px] text-[rgb(var(--color-text-muted))]">
          This balance follows the person across plan amendments and plan
          versions. Year-end alone does not reset it; only a posted ledger event
          such as forgiveness, settlement, transfer, recovery, or adjustment
          changes the balance.
        </p>
      </div>
    </section>
  );
}

function LedgerHeader({
  ledger,
  mode,
  personUuid,
  fiscalPeriodId,
  onEscalate,
}) {
  const balance = ledger.currentBalance;

  const balanceState =
    balance > 0 ? 'outstanding' : balance === 0 ? 'cleared' : 'overpayment';

  const balanceClass =
    balance > 0
      ? 'text-[rgb(var(--color-danger))]'
      : balance === 0
        ? 'text-[rgb(var(--color-success))]'
        : 'text-[rgb(var(--color-warning))]';

  const balanceText =
    balance === 0
      ? `${formatCurrency(balance, ledger.currencyCode)} · Cleared`
      : balance > 0
        ? `${formatCurrency(balance, ledger.currencyCode)} outstanding`
        : `${formatCurrency(
            Math.abs(balance),
            ledger.currencyCode,
          )} overpayment credit`;

  const canEscalate =
    mode === 'manager' &&
    typeof onEscalate === 'function' &&
    balance !== 0 &&
    ledger.ledgerStatus === 'open';

  return (
    <header className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
              Draw Balance
            </h2>

            <span
              className={[
                'rounded-[999px]',
                'bg-[rgb(var(--color-info)/0.1)]',
                'px-2 py-0.5 text-[10px] font-semibold',
                'text-[rgb(var(--color-info))]',
              ].join(' ')}
            >
              Person-scoped
            </span>

            <span
              className={[
                'rounded-[999px]',
                'bg-[rgb(var(--color-surface-muted))]',
                'px-2 py-0.5 text-[10px]',
                'text-[rgb(var(--color-text-muted))]',
              ].join(' ')}
            >
              {humanLedgerStatus(ledger.ledgerStatus)}
            </span>
          </div>

          <p
            aria-label={`${formatCurrency(
              balance,
              ledger.currencyCode,
            )}. Draw balance is ${balanceState}.`}
            className={`mt-2 text-[18px] font-semibold ${balanceClass}`}
          >
            {balanceText}
          </p>

          <p className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
            Recoverable draw · Person-level balance · Continues across plan
            amendments
          </p>

          {ledger.programCode && (
            <p className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
              Program: {ledger.programCode}
            </p>
          )}
        </div>

        {canEscalate && (
          <button
            type="button"
            className="secondary-button"
            onClick={() =>
              onEscalate({
                personUuid,
                fiscalPeriodId,
                currentBalance: ledger.currentBalance,
                currencyCode: ledger.currencyCode,
                ledgerStatus: ledger.ledgerStatus,
              })
            }
          >
            Escalate
          </button>
        )}
      </div>
    </header>
  );
}

function LedgerRow({ entry, mode }) {
  const typeLabel = ENTRY_TYPE_LABELS[entry.entryType] || entry.entryType;

  const amountClass =
    entry.amount > 0
      ? 'text-[rgb(var(--color-danger))]'
      : entry.amount < 0
        ? 'text-[rgb(var(--color-success))]'
        : 'text-[rgb(var(--color-text-muted))]';

  const balanceClass =
    entry.balanceAfter > 0
      ? 'text-[rgb(var(--color-danger))]'
      : entry.balanceAfter === 0
        ? 'text-[rgb(var(--color-success))]'
        : 'text-[rgb(var(--color-warning))]';

  const governedEntry =
    entry.entryType === 'forgiveness' ||
    entry.entryType === 'adjustment';

  return (
    <tr className="border-t border-[rgb(var(--color-border))]">
      <td className="px-4 py-3 align-top text-[12px] text-[rgb(var(--color-text))]">
        {formatDate(entry.effectiveDate)}
      </td>

      <td className="px-4 py-3 align-top text-[12px] text-[rgb(var(--color-text))]">
        {entry.fiscalPeriodLabel}
      </td>

      <td className="px-4 py-3 align-top">
        <div
          aria-label={typeLabel}
          className="text-[12px] font-medium text-[rgb(var(--color-text))]"
        >
          {typeLabel}
        </div>

        {entry.planLabel && (
          <span
            aria-label={`Plan context at time of ledger entry: ${entry.planLabel}`}
            className={[
              'mt-1 inline-flex rounded-[999px]',
              'bg-[rgb(var(--color-surface-muted))]',
              'px-2 py-0.5 text-[10px]',
              'text-[rgb(var(--color-text-muted))]',
            ].join(' ')}
          >
            {entry.planLabel}
          </span>
        )}

        {entry.reasonText && (
          <p className="mt-1 max-w-[360px] text-[11px] text-[rgb(var(--color-text-muted))]">
            {entry.reasonText}
          </p>
        )}

        {mode === 'manager' &&
          governedEntry &&
          (entry.approvedBy || entry.approvedAt) && (
            <p className="mt-1 text-[10px] text-[rgb(var(--color-text-muted))]">
              Governed entry
              {entry.approvedBy ? ` · Approved by ${entry.approvedBy}` : ''}
              {entry.approvedAt
                ? ` · ${fullDateTime(entry.approvedAt) ?? entry.approvedAt}`
                : ''}
            </p>
          )}
      </td>

      <td
        className={`px-4 py-3 text-right align-top text-[12px] font-semibold ${amountClass}`}
      >
        {formatSignedCurrency(entry.amount, entry.currencyCode)}
      </td>

      <td
        className={`px-4 py-3 text-right align-top text-[12px] font-semibold ${balanceClass}`}
      >
        {entry.balanceAfter === 0
          ? `${formatCurrency(
              entry.balanceAfter,
              entry.currencyCode,
            )} · Cleared`
          : formatCurrency(entry.balanceAfter, entry.currencyCode)}
      </td>
    </tr>
  );
}

function LedgerSkeleton({ className }) {
  return (
    <section
      aria-busy="true"
      aria-label="Loading draw balance ledger"
      className={[
        'rounded-[12px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface))]',
        'p-4',
        className,
      ].join(' ')}
    >
      <div className="skeleton h-4 w-28 rounded" />
      <div className="skeleton mt-3 h-6 w-40 rounded" />
      <div className="skeleton mt-2 h-3 w-72 max-w-full rounded" />

      <div className="mt-5 space-y-3">
        <div className="skeleton h-9 w-full rounded" />
        <div className="skeleton h-9 w-full rounded" />
        <div className="skeleton h-9 w-full rounded" />
      </div>
    </section>
  );
}

const ENTRY_TYPE_LABELS = {
  advance: 'Draw advance',
  recovery: 'Recovery',
  forgiveness: 'Forgiven',
  adjustment: 'Adjustment',
  conversion: 'Currency conversion',
  settlement: 'Year-end settlement',
  transfer: 'Balance transfer',
};

function humanLedgerStatus(status) {
  return {
    open: 'Open ledger',
    closed: 'Closed ledger',
    transferred: 'Transferred',
    settled: 'Settled',
  }[status] || status;
}

function formatCurrency(amount, currencyCode = 'USD') {
  if (amount === null || amount === undefined) return null;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatSignedCurrency(amount, currencyCode = 'USD') {
  if (amount === null || amount === undefined) return null;

  const formatted = formatCurrency(Math.abs(amount), currencyCode);

  if (amount > 0) return `+${formatted}`;
  if (amount < 0) return `−${formatted}`;
  return formatted;
}

function formatDate(isoDate) {
  if (!isoDate) return '—';

  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}